import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import { Container, Section, Button, theme } from '../../styles/GlobalStyles';

const PageHeader = styled.section`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, #003d7a 100%);
  color: white;
  padding: 120px 0 80px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="%23ffffff" fill-opacity="0.05"><circle cx="30" cy="30" r="2"/></g></g></svg>');
  }
`;

const HeaderContent = styled.div`
  position: relative;
  z-index: 1;
`;

const PageTitle = styled(motion.h1)`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 20px;
  line-height: 1.2;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2.2rem;
  }
`;

const PageSubtitle = styled(motion.p)`
  font-size: 1.3rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 60px;
  align-items: start;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const MainContent = styled.div``;

const SidebarContent = styled.div`
  position: sticky;
  top: 100px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    position: static;
  }
`;

const ContentSection = styled(motion.div)`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.dark};
  margin-bottom: 20px;
`;

const SectionContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.7;
  color: ${theme.colors.gray};
  margin-bottom: 20px;
`;

const BenefitsList = styled.ul`
  list-style: none;
  margin: 20px 0;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 1rem;
  color: ${theme.colors.dark};

  svg {
    color: ${theme.colors.primary};
    font-size: 1.2rem;
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

const CTACard = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary}, #003d7a);
  color: white;
  padding: 30px;
  border-radius: 15px;
  text-align: center;
  margin-bottom: 30px;
`;

const CTATitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 15px;
`;

const CTADescription = styled.p`
  margin-bottom: 20px;
  opacity: 0.9;
  line-height: 1.6;
`;

const InfoCard = styled.div`
  background: white;
  border: 1px solid ${theme.colors.lightGray};
  border-radius: 10px;
  padding: 25px;
  margin-bottom: 20px;
`;

const InfoTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${theme.colors.dark};
  margin-bottom: 10px;
`;

const InfoContent = styled.p`
  font-size: 0.95rem;
  color: ${theme.colors.gray};
  line-height: 1.6;
`;

interface ServiceTemplateProps {
  title: string;
  subtitle: string;
  sections: {
    title: string;
    content: string | React.ReactNode;
    benefits?: string[];
  }[];
  ctaTitle?: string;
  ctaDescription?: string;
  sidebarInfo?: {
    title: string;
    content: string;
  }[];
}

const ServiceTemplate: React.FC<ServiceTemplateProps> = ({
  title,
  subtitle,
  sections,
  ctaTitle = "Ready to Get Started?",
  ctaDescription = "Contact our experts to learn more about how we can help secure your organization.",
  sidebarInfo = []
}) => {
  return (
    <>
      <PageHeader>
        <Container>
          <HeaderContent>
            <PageTitle
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {title}
            </PageTitle>
            <PageSubtitle
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {subtitle}
            </PageSubtitle>
          </HeaderContent>
        </Container>
      </PageHeader>

      <Section>
        <Container>
          <ContentGrid>
            <MainContent>
              {sections.map((section, index) => (
                <ContentSection
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <SectionTitle>{section.title}</SectionTitle>
                  <SectionContent>
                    {typeof section.content === 'string' ? (
                      <p>{section.content}</p>
                    ) : (
                      section.content
                    )}
                  </SectionContent>
                  {section.benefits && (
                    <BenefitsList>
                      {section.benefits.map((benefit, benefitIndex) => (
                        <BenefitItem key={benefitIndex}>
                          <FaCheckCircle />
                          {benefit}
                        </BenefitItem>
                      ))}
                    </BenefitsList>
                  )}
                </ContentSection>
              ))}
            </MainContent>

            <SidebarContent>
              <CTACard>
                <CTATitle>{ctaTitle}</CTATitle>
                <CTADescription>{ctaDescription}</CTADescription>
                <Button variant="secondary">
                  Contact Us <FaArrowRight />
                </Button>
              </CTACard>

              {sidebarInfo.map((info, index) => (
                <InfoCard key={index}>
                  <InfoTitle>{info.title}</InfoTitle>
                  <InfoContent>{info.content}</InfoContent>
                </InfoCard>
              ))}
            </SidebarContent>
          </ContentGrid>
        </Container>
      </Section>
    </>
  );
};

export default ServiceTemplate;

